import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Truck, Plus, RefreshCw, Pencil, Trash2, Phone, Mail } from 'lucide-react';

const FornecedoresPage = () => {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: '', cnpj_cpf: '', telefone: '', email: '', endereco: '', observacoes: '' });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/fornecedores');
      setItens(r.data.itens || []);
    } catch (e) {
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNovo = () => {
    setEditing(null);
    setForm({ nome: '', cnpj_cpf: '', telefone: '', email: '', endereco: '', observacoes: '' });
    setShowForm(true);
  };

  const abrirEdicao = (f) => {
    setEditing(f);
    setForm({
      nome: f.nome || '', cnpj_cpf: f.cnpj_cpf || '', telefone: f.telefone || '',
      email: f.email || '', endereco: f.endereco || '', observacoes: f.observacoes || '',
    });
    setShowForm(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      if (editing) {
        await api.patch(`/fornecedores/${editing.id}`, form);
        toast.success('Fornecedor atualizado');
      } else {
        await api.post('/fornecedores', form);
        toast.success('Fornecedor cadastrado');
      }
      setShowForm(false);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Remover este fornecedor?')) return;
    try {
      await api.delete(`/fornecedores/${id}`);
      toast.success('Fornecedor removido');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="fornecedores-page">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Fornecedores</h1>
          <p className="text-slate-600">Cadastro de fornecedores para vincular às contas a pagar</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={carregar} data-testid="btn-refresh-fornecedores">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
          <Button onClick={abrirNovo} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-novo-fornecedor">
            <Plus className="w-4 h-4 mr-2" /> Novo fornecedor
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tabela-fornecedores">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium">Nome</th>
                <th className="text-left p-3 font-medium">CNPJ/CPF</th>
                <th className="text-left p-3 font-medium">Contato</th>
                <th className="text-center p-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="p-8 text-center text-slate-500">Carregando...</td></tr>
              ) : itens.length === 0 ? (
                <tr><td colSpan="4" className="p-8 text-center text-slate-500">Nenhum fornecedor cadastrado.</td></tr>
              ) : itens.map((f) => (
                <tr key={f.id} className="border-b hover:bg-slate-50" data-testid={`row-fornecedor-${f.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2 font-medium">
                      <Truck className="w-4 h-4 text-slate-400" /> {f.nome}
                    </div>
                    {f.endereco && <div className="text-xs text-slate-500 ml-6">{f.endereco}</div>}
                  </td>
                  <td className="p-3 text-slate-600">{f.cnpj_cpf || '-'}</td>
                  <td className="p-3 text-slate-600">
                    {f.telefone && <div className="flex items-center gap-1 text-xs"><Phone className="w-3 h-3" /> {f.telefone}</div>}
                    {f.email && <div className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" /> {f.email}</div>}
                    {!f.telefone && !f.email && '-'}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex justify-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => abrirEdicao(f)} data-testid={`btn-edit-fornecedor-${f.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => excluir(f.id)} data-testid={`btn-del-fornecedor-${f.id}`}>
                        <Trash2 className="w-3 h-3 text-rose-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="modal-fornecedor">
          <DialogHeader><DialogTitle>{editing ? 'Editar fornecedor' : 'Novo fornecedor'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="input-forn-nome" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CNPJ/CPF</Label>
                <Input value={form.cnpj_cpf} onChange={(e) => setForm({ ...form, cnpj_cpf: e.target.value })} />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>E-mail</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Endereço</Label>
              <Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
            </div>
            <div>
              <Label>Observações</Label>
              <Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={salvar} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-save-fornecedor">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FornecedoresPage;
